import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import { useProduct } from 'api/useProduct';
import { EFeatureFlag, EPopupSubType, OfferType } from 'constants/enums';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { useProgressBarForm } from 'views/Offers/hooks/useProgressBarForm';
import { useProgressBarOffers } from 'views/Offers/hooks/useProgressBarOffers';
import { DEFAULT_OFFER_DESIGN_EXTERNAL_ID } from 'views/Offers/Offer.consts';
import { checkUniqueness } from 'views/Offers/Offer.utils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import DialogModal from 'components/DialogModal/DialogModal';
import { Form } from 'components/ui';

import PreviewSection from './components/Sections/PreviewSection';
import { StandardPopupsContent } from './components/StandardPopupsContent';
import { TriggeredPopupsContent } from './components/TriggeredPopupsContent';
import { usePopupFormInit } from './hooks/usePopupFormInit';
import { usePopupOffersIds } from './hooks/usePopupOffersIds';
import { usePopupProducts } from './hooks/usePopupProducts';
import {
  DEFAULT_VALUES,
  FORM_SCHEMA,
  PopupsFormFields
} from './PopupsForm.consts';
import { buildPopupPayload } from './PopupsForm.utils';

interface IPopupsFormV2Props {
  edit?: boolean;
  dup?: boolean;
}

const PopupsFormV2: FC<IPopupsFormV2Props> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId, popupId } = useParams();

  const { featureFlags } = useFeatureFlags({ publisherId });
  const hasFeatureFlagTags = featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];
  const isTriggeredPopups =
    !!featureFlags?.[EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU];
  let offerLabel = 'Popups';
  if (isTriggeredPopups) {
    offerLabel = 'Triggered Popup';
  }

  const navigate = useNavigate();
  const { getProducts } = useProduct(publisherId);
  const {
    addPopUpOffer,
    updatePopUpOffer,
    getOffer,
    getOffers,
    formatProductQuantity
  } = useOffers({ offerId: popupId, offerType: OfferType.POPUP });

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel,
    addOffer: addPopUpOffer,
    updateOffer: updatePopUpOffer,
    onSuccess: (): void => {
      getOffers.refetch();
    }
  });

  const { getImages } = useImages(publisherId);
  const { offerDesignOptions, defaultOfferDesignId: defaultOfferUiId } =
    useOfferDesignOptions({
      publisherId: publisherId || '',
      offerDesignType: isTriggeredPopups
        ? OfferDesignTypes.TriggeredPopup
        : OfferDesignTypes.PopUp,
      defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.POPUP]
    });
  const [activeTab, setActiveTab] = useState('settings');
  const [startHour, setStartHour] = useState('');
  const [intervalCron, setIntervalCron] = useState('');
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);

  let buttonText = 'Save';
  if (edit) {
    buttonText = 'Update';
  }
  const offers = useMemo(
    () => getOffers.data?.offers || [],
    [getOffers.data?.offers]
  );
  const currentOffer = getOffer.data?.result;

  const { getOffers: getBundleOffers } = useOffers({
    offerType: OfferType.BUNDLE
  });

  const { getOffers: getPromotions } = useOffers({
    offerType: OfferType.SPECIAL_OFFER
  });

  const form = useForm<PopupsFormFields>({
    resolver: zodResolver(FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      ...DEFAULT_VALUES,
      ...(isTriggeredPopups && { subType: EPopupSubType.POST_PURCHASE })
    }
  });

  const subType = form.watch('subType');
  const isDailyBonus = subType === EPopupSubType.DAILY_BONUS;
  const isSingleProductMode = subType === EPopupSubType.POST_PURCHASE;
  const {
    productsList,
    addProduct,
    deleteProduct,
    updateProduct,
    reorderProducts,
    setMultiProducts,
    setSingleProducts,
    peekActiveListForMode
  } = usePopupProducts(isSingleProductMode);

  const {
    progressBarFormValues,
    setProgressBarFormValues,
    isProgressBarFormValid,
    dirtyProgressBar,
    handleProgressBarFormChange
  } = useProgressBarForm();

  const { progressBarOffers } = useProgressBarOffers();

  const isNameTaken = !checkUniqueness(
    form.watch('name'),
    'name',
    offers,
    currentOffer,
    edit
  );
  const isPublisherOfferIdTaken = !checkUniqueness(
    form.watch('publisherOfferId'),
    'publisherOfferId',
    offers,
    currentOffer,
    edit
  );

  useEffect(() => {
    form.register('products');
  }, [form, form.register]);

  const currentOfferUI = useWatch({
    control: form.control,
    name: 'offerUiId',
    defaultValue: defaultOfferUiId
  });

  usePopupFormInit({
    form,
    edit,
    dup,
    popupId,
    defaultOfferUiId,
    currentOffer: getOffer.data?.result,
    setMultiProducts,
    setSingleProducts,
    setProgressBarFormValues
  });

  const onSubmit = useCallback(
    (data: PopupsFormFields): void => {
      const payload = buildPopupPayload({
        data,
        productsList,
        progressBarFormValues
      });
      submitOffer(payload, popupId);
    },
    [productsList, progressBarFormValues, submitOffer, popupId]
  );

  const { offersIds } = usePopupOffersIds({
    bundleOffers: getBundleOffers?.data?.offers,
    promotionOffers: getPromotions?.data?.offers
  });

  const watchedName = form.watch('name');
  const watchedPrice = form.watch('price');

  const formTitle = useMemo((): string => {
    const titleSuffix = isTriggeredPopups ? '' : ' Offer';
    if (edit) {
      return `Edit ${offerLabel}${titleSuffix} (${watchedName || currentOffer?.name || ''})`;
    }
    if (dup) {
      return `Duplicate ${offerLabel}${titleSuffix}`;
    }
    return `Create ${offerLabel}${titleSuffix}`;
  }, [edit, dup, offerLabel, watchedName, currentOffer?.name, isTriggeredPopups]);

  const hasRequiredProducts =
    isDailyBonus || !isTriggeredPopups || productsList.length > 0;

  const isFormValid = useMemo(
    (): boolean =>
      form.formState.isValid &&
      isProgressBarFormValid &&
      hasRequiredProducts &&
      (dup || form.formState.isDirty || dirtyProgressBar) &&
      !isNameTaken &&
      !isPublisherOfferIdTaken,
    [
      form.formState.isValid,
      isProgressBarFormValid,
      hasRequiredProducts,
      form.formState.isDirty,
      dup,
      dirtyProgressBar,
      isNameTaken,
      isPublisherOfferIdTaken
    ]
  );

  const submitForm = useCallback((): void => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const handleSave = useCallback((): void => {
    if (edit && currentOffer?.active) {
      setIsUpdateDialogOpen(true);
      return;
    }
    submitForm();
  }, [edit, currentOffer, submitForm]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  const isImagesReady = !getImages.isLoading && !!getImages.data;

  let formContent: React.ReactNode = null;
  if (isTriggeredPopups) {
    formContent = (
      <TriggeredPopupsContent
        form={form}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        offerDesignOptions={offerDesignOptions}
        isDailyBonus={isDailyBonus}
        isNameTaken={isNameTaken}
        isPublisherOfferIdTaken={isPublisherOfferIdTaken}
        offersIds={offersIds}
        getProducts={getProducts}
        productsList={productsList}
        addProduct={addProduct}
        deleteProduct={deleteProduct}
        updateProduct={updateProduct}
        reorderProducts={reorderProducts}
        peekActiveListForMode={peekActiveListForMode}
        formatProductQuantity={formatProductQuantity}
        startHour={startHour}
        setStartHour={setStartHour}
        intervalCron={intervalCron}
        setIntervalCron={setIntervalCron}
        progressBarInitialValues={progressBarFormValues}
        onProgressBarChange={handleProgressBarFormChange}
        progressBarOffers={progressBarOffers}
      />
    );
  } else {
    formContent = (
      <StandardPopupsContent
        form={form}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        offerDesignOptions={offerDesignOptions}
        isNameTaken={isNameTaken}
        isPublisherOfferIdTaken={isPublisherOfferIdTaken}
        hasFeatureFlagTags={!!hasFeatureFlagTags}
        getProducts={getProducts}
        productsList={productsList}
        addProduct={addProduct}
        deleteProduct={deleteProduct}
        updateProduct={updateProduct}
        formatProductQuantity={formatProductQuantity}
      />
    );
  }

  const updateDialogActions = useMemo(
    () => ({
      onClose: {
        label: 'Cancel',
        action: (): void => setIsUpdateDialogOpen(false)
      },
      onConfirm: {
        label: 'Update',
        action: (): void => {
          setIsUpdateDialogOpen(false);
          submitForm();
        }
      }
    }),
    [submitForm]
  );

  return (
    <React.Fragment>
      <OfferFormLayout
        title={formTitle}
        mainButtonText={buttonText}
        isFormValid={isFormValid}
        onSave={handleSave}
        onClose={handleClose}
        previewContent={
          <PreviewSection
            publisherId={publisherId}
            currentOfferUI={currentOfferUI}
            isDailyBonus={isDailyBonus}
            isTriggeredPopup={isTriggeredPopups}
            currentPrice={watchedPrice}
            products={getProducts.data}
            productsList={productsList}
            progressBarFormValues={progressBarFormValues}
            progressBarOffers={progressBarOffers}
          />
        }
      >
        {isImagesReady && (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="mt-7 mb-9 w-full px-24 flex flex-col gap-y-9"
            >
              {formContent}
            </form>
          </Form>
        )}
      </OfferFormLayout>
      <DialogModal
        isOpen={isUpdateDialogOpen}
        title="Popup is currently live"
        description="Your changes will update automatically"
        modalActions={updateDialogActions}
      />
    </React.Fragment>
  );
};

export { PopupsFormV2 };
